import React, { useState, useEffect, useCallback, useMemo } from "react";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import styled from "styled-components";
import { FiRefreshCw } from "react-icons/fi";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { updateCourseInFirestore, updateIsActive } from "../../../admin";
import {
    getCourseTimeZone,
    getTimeZoneOptions,
    shiftToTimeZone,
    toZonedDayjs,
} from "../../../utils/courseSchedule";
import { toast } from "sonner";
import {
    SettingsContainer,
    Header,
    Card,
    ButtonRow,
    PrimaryButton,
    SecondaryButton,
} from "./styles/shared";

const dateEq = (a, b) => {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return new Date(a).getTime() === new Date(b).getTime();
};

const GeneralSettings = ({ courseId, course, onCourseUpdate, onDirtyChange, onRefresh }) => {
    const [editedCourse, setEditedCourse] = useState({
        name: "",
        description: "",
        startDate: null,
        endDate: null,
        timeZone: getCourseTimeZone(null),
    });
    const [savingBasic, setSavingBasic] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const savedValues = useMemo(
        () =>
            course
                ? {
                    name: course.name ?? "",
                    description: course.description ?? "",
                    startDate: course.startDate ? new Date(course.startDate) : null,
                    endDate: course.endDate ? new Date(course.endDate) : null,
                    timeZone: getCourseTimeZone(course),
                }
                : null,
        [course?.id, course?.name, course?.description, course?.startDate, course?.endDate, course?.timeZone]
    );

    useEffect(() => {
        if (!savedValues) return;
        setEditedCourse(savedValues);
        setInitialized(true);
    }, [savedValues]);

    const isDirty = useMemo(() => {
        if (!savedValues || !initialized) return false;
        return (
            editedCourse.name !== savedValues.name ||
            editedCourse.description !== savedValues.description ||
            !dateEq(editedCourse.startDate, savedValues.startDate) ||
            !dateEq(editedCourse.endDate, savedValues.endDate) ||
            editedCourse.timeZone !== savedValues.timeZone
        );
    }, [editedCourse, savedValues]);

    const isFieldDirty = useCallback(
        (field) => {
            if (!savedValues || !initialized) return false;
            if (field === "startDate" || field === "endDate") return !dateEq(editedCourse[field], savedValues[field]);
            return editedCourse[field] !== savedValues[field];
        },
        [editedCourse, savedValues]
    );

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);

    const saveBasic = useCallback(
        async (payload) => {
            if (!courseId) return;
            setSavingBasic(true);
            try {
                await updateCourseInFirestore(courseId, payload);
                onCourseUpdate(payload);
                toast.success("Course info saved.");
            } catch (err) {
                console.error(err);
                toast.error("Failed to save course info.");
            } finally {
                setSavingBasic(false);
            }
        },
        [courseId, onCourseUpdate]
    );

    const handleBasicChange = (e) => {
        const { name, value } = e.target;
        setEditedCourse((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (field, value) => {
        setEditedCourse((prev) => ({ ...prev, [field]: value }));
    };

    // Keep the picked wall-clock times when switching zones
    const handleTimeZoneChange = (e) => {
        const nextZone = e.target.value;
        setEditedCourse((prev) => ({
            ...prev,
            startDate: shiftToTimeZone(prev.startDate, prev.timeZone, nextZone),
            endDate: shiftToTimeZone(prev.endDate, prev.timeZone, nextZone),
            timeZone: nextZone,
        }));
    };

    const handleCancel = useCallback(() => {
        if (savedValues) setEditedCourse(savedValues);
    }, [savedValues]);

    const doRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            const updated = await onRefresh();

            if (updated) {
                setEditedCourse({
                    name: updated.name ?? "",
                    description: updated.description ?? "",
                    startDate: updated.startDate ? new Date(updated.startDate) : null,
                    endDate: updated.endDate ? new Date(updated.endDate) : null,
                    timeZone: getCourseTimeZone(updated),
                });
            }

            toast.success("Course info refreshed.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to refresh course info.");
        } finally {
            setRefreshing(false);
        }
    }, [onRefresh]);

    const handleRefreshClick = useCallback(() => {
        if (!onRefresh) return;
        if (isDirty) {
            setShowRefreshConfirm(true);
        } else {
            doRefresh();
        }
    }, [onRefresh, isDirty, doRefresh]);

    const handleIsActiveToggle = async () => {
        const next = !course?.isActive;
        try {
            await updateIsActive(courseId, next);
            onCourseUpdate({ isActive: next });
            toast.success(next ? "Course is now active." : "Course is now inactive.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update course status.");
        }
    };

    const timeZone = editedCourse.timeZone;
    const startVal = toZonedDayjs(editedCourse.startDate, timeZone);
    const endVal = toZonedDayjs(editedCourse.endDate, timeZone);
    const timeZoneOptions = getTimeZoneOptions();

    return (
        <>
        {showRefreshConfirm && (
            <ConfirmDialog
                title="Discard Changes?"
                message="You have unsaved changes. Refresh will discard them. Continue?"
                confirmText="Yes, Refresh"
                cancelText="No, Go Back"
                variant="primary"
                onConfirm={() => { setShowRefreshConfirm(false); doRefresh(); }}
                onCancel={() => setShowRefreshConfirm(false)}
            />
        )}
        <SettingsContainer>
            <Header>
                <div>
                    <h2>Edit Course</h2>
                    <p> Update the basic information for your course.</p>
                </div>
                {onRefresh && (
                    <RefreshButton
                        type="button"
                        onClick={handleRefreshClick}
                        disabled={savingBasic || refreshing}
                        title="Refresh course data"
                        aria-label="Refresh"
                    >
                        <FiRefreshCw size={18} className={refreshing ? "spin" : ""} />
                    </RefreshButton>
                )}
            </Header>
            <Card>
                <SectionTitle>Basic course info</SectionTitle>

                <FormGrid>
                    {/* Course Name */}
                    <Field>
                        <FieldLabel>Course Name</FieldLabel>
                        <StyledInput
                            type="text"
                            name="name"
                            value={editedCourse.name}
                            onChange={handleBasicChange}
                            placeholder="e.g. Introduction to Computer Science"
                            $dirty={isFieldDirty("name")}
                        />
                    </Field>

                    {/* Description */}
                    <Field>
                        <FieldLabel>Description</FieldLabel>
                        <StyledInput
                            type="text"
                            name="description"
                            value={editedCourse.description}
                            onChange={handleBasicChange}
                            placeholder="Course description"
                            $dirty={isFieldDirty("description")}
                        />
                    </Field>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {/* Start Date */}
                        <Field>
                            <FieldLabel>Start</FieldLabel>
                            <StyledDatePickerWrapper
                                $dirty={isFieldDirty("startDate")}>
                                <DateTimePicker
                                    value={startVal}
                                    timezone={timeZone}
                                    referenceDate={dayjs().tz(timeZone).startOf("day")}
                                    onChange={(v) =>
                                        handleDateChange("startDate", v ? v.toDate() : null)
                                    }
                                    slotProps={{
                                        popper: { disablePortal: true },
                                        textField: { fullWidth: true },
                                    }}
                                />
                            </StyledDatePickerWrapper>
                        </Field>

                        {/* End Date */}
                        <Field>
                            <FieldLabel>End (optional)</FieldLabel>
                            <StyledDatePickerWrapper $dirty={isFieldDirty("endDate")}>
                                <DateTimePicker
                                    value={endVal}
                                    timezone={timeZone}
                                    referenceDate={dayjs().tz(timeZone).endOf("day")}
                                    minDateTime={startVal}
                                    onChange={(v) =>
                                        handleDateChange("endDate", v ? v.toDate() : null)
                                    }
                                    slotProps={{
                                        popper: { disablePortal: true },
                                        field: {
                                            clearable: true,
                                            onClear: () => handleDateChange("endDate", null),
                                        },
                                        actionBar: {
                                            // Listing actions replaces MUI's default, so keep OK alongside Clear
                                            actions: ['clear', 'accept'],
                                            sx: {
                                                // style all action buttons
                                                '& .MuiButton-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '1rem',
                                                    borderRadius: 1.5,
                                                    color: '#9333ea',
                                                    '&:hover': { backgroundColor: 'rgba(147,51,234,0.08)' },
                                                },
                                            },
                                        },
                                    }}
                                />
                            </StyledDatePickerWrapper>
                        </Field>
                    </LocalizationProvider>

                    {/* Time Zone */}
                    <Field>
                        <FieldLabel htmlFor="course-time-zone">Time Zone</FieldLabel>
                        <StyledSelect
                            id="course-time-zone"
                            value={timeZone}
                            onChange={handleTimeZoneChange}
                            $dirty={isFieldDirty("timeZone")}
                        >
                            {timeZoneOptions.some((tz) => tz.value === timeZone) ? null : (
                                <option value={timeZone}>{timeZone}</option>
                            )}
                            {timeZoneOptions.map((tz) => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </StyledSelect>
                        <FieldHint>Start and end times use this time zone.</FieldHint>
                    </Field>
                </FormGrid>
                <ButtonRow>
                    <SecondaryButton variant="secondary" onClick={handleCancel} disabled={!isDirty || savingBasic}>
                        Discard Changes
                    </SecondaryButton>
                    <PrimaryButton onClick={() => saveBasic(editedCourse)} disabled={savingBasic || !isDirty}>
                        {savingBasic ? "Saving..." : "Save Changes"}
                    </PrimaryButton>
                </ButtonRow>
            </Card>
        </SettingsContainer>
        </>
    );
};

export default GeneralSettings;

const RefreshButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    flex-shrink: 0;
    &:hover:not(:disabled) {
        background: #f8fafc;
        color: #9333ea;
        border-color: #e2e8f0;
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    .spin {
        animation: refreshSpin 0.8s linear infinite;
    }
    @keyframes refreshSpin {
        to { transform: rotate(360deg); }
    }
`;

const SectionTitle = styled.h2`
    font-size: 1.25rem;
    margin: 0 0 16px 0;
    color: #111827;
    text-align: left;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 20px;

    @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    text-align: left;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid ${({ $dirty }) => ($dirty ? "#dc2626" : "#e5e7eb")};
    background: white;
    font-size: 14px;
    transition: all 0.2s;
    box-shadow: ${({ $dirty }) => ($dirty ? "0 0 0 1px #dc2626" : "none")};

    &:focus {
        outline: none;
        border-color: ${({ $dirty }) => ($dirty ? "#dc2626" : "#9333ea")};
        box-shadow: 0 0 0 2px ${({ $dirty }) => ($dirty ? "rgba(220, 38, 38, 0.25)" : "rgba(147, 51, 234, 0.25)")};
    }
`;

const StyledSelect = styled(StyledInput).attrs({ as: "select" })`
    font-family: inherit;
    cursor: pointer;
`;

const FieldHint = styled.span`
    font-size: 12px;
    color: #6b7280;
    text-align: left;
`;

const StyledDatePickerWrapper = styled.div`
    margin: 5px 0;
    width: 100%;
    .MuiTextField-root {
        width: 100%;
    }
    .MuiInputBase-root {
        height: 40px;
    }
    .MuiInputLabel-root {
        margin-top: -5px;
        background-color: #ffffffff;
        font-family: "Inter", sans-serif;
        font-size: 14px;
    }
    .MuiInputLabel-root.Mui-focused {
        color: ${({ $dirty }) => ($dirty ? "#dc2626" : "#9333ea")};
        font-weight: 500;
    }

    /* border */
    .MuiOutlinedInput-root {
        fieldset {
            border-color: ${({ $dirty }) => ($dirty ? "#dc2626" : "#ccc")};
            border-width: ${({ $dirty }) => ($dirty ? "2px" : "1px")};
        }
        &:hover fieldset {
            border-color: ${({ $dirty }) => ($dirty ? "#b91c1c" : "#b189d5ff")};
        }
        &.Mui-focused fieldset {
            border-color: ${({ $dirty }) => ($dirty ? "#dc2626" : "#9333ea")};
        }
    }

    /* selected highlight in calendar */
    .MuiPickersDay-root.Mui-selected {
        background-color: #9333ea; /* fill */
        color: #fff;               /* text */
    }

    /* selected + hover */
    .MuiPickersDay-root.Mui-selected:hover {
    background-color: #a074cb;
    }
    /* plain hover (not selected) */
    .MuiPickersDay-root:hover {
    background-color: rgba(177, 137, 213, 0.15);
    }

    /* Any selected day */
    .MuiPickersDay-root.Mui-selected {
    background-color: #9333ea;
    color: #fff;
    }

    .MuiPickersDay-root.Mui-selected:not(.MuiPickersDay-today) { 
    background-color: #9333ea; 
    color: #fff; 
    }

    /* Selected + today (override MUI's blue) */
    .MuiPickersDay-root.Mui-selected.MuiPickersDay-today {
    background-color: #9333ea; /* same purple as selected */
    color: #fff;
    border: 2px solid #9333ea; /* keep the today ring if you want */
    }

    /* Today but not selected */
    .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid #9333ea;
    background-color: transparent;
    }

    /* Selected year */
    .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-selected {
    background-color: #9333ea;
    color: #fff;
    }

    /* Hover on any year button */
    .MuiYearCalendar-root .MuiPickersYear-yearButton:hover {
    background-color: #d5c1e6ff;
    }

    /* Today outline in year view */
    .MuiYearCalendar-root .MuiPickersYear-yearButton.MuiPickersYear-today {
    border: 2px solid #9333ea;
    }

    /* Selected hour / minute / AM-PM in the time columns */
    .MuiMultiSectionDigitalClockSection-item.Mui-selected,
    .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover {
    background-color: #9333ea;
    color: #fff;
    }

    /* Start picker's default OK button */
    .MuiPickersLayout-actionBar .MuiButton-root {
    color: #9333ea;
    }

`;


